// src/scenes/.../CBOnboarding.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Divider, Snackbar,
  Alert, IconButton, Tooltip, CircularProgress, useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../../theme';

// RTK Query hooks (added below in dataAssetsApiSlice)
import {
  useListCbUsersQuery,
  useCreateCbUserMutation,
  useDeleteCbUserMutation,
} from '../../../features/data_assets/dataAssetsApiSlice';

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CBOnboarding = ({ organization, onComplete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const orgId = organization?.organization_id;

  const [email, setEmail] = useState('');
  const [name,  setName]  = useState('');
  const [pwd,   setPwd]   = useState('');

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  const { data, isFetching, refetch } = useListCbUsersQuery({ org_id: orgId }, { skip: !orgId });
  const [createCbUser, { isLoading: creating }] = useCreateCbUserMutation();
  const [deleteCbUser, { isLoading: deleting  }] = useDeleteCbUserMutation();

  const users = useMemo(() => data?.items || [], [data]);

  const canCreate = emailRe.test(email) && pwd.length >= 8;

  async function onCreate() {
    try {
      await createCbUser({ org_id: orgId, email, name, password: pwd }).unwrap();
      setSnack({ open: true, msg: 'User created', sev: 'success' });
      setEmail(''); setName(''); setPwd('');
      refetch();
    } catch (e) {
      setSnack({ open: true, msg: e?.data?.message || 'Failed to create user', sev: 'error' });
    }
  }

  async function onRemove(userId) {
    try {
      await deleteCbUser({ id: userId }).unwrap();
      setSnack({ open: true, msg: 'User removed', sev: 'success' });
      refetch();
    } catch (e) {
      setSnack({ open: true, msg: e?.data?.message || 'Failed to remove user', sev: 'error' });
    }
  }

  return (
    <Paper elevation={6} sx={{
      p: 3, borderRadius: 2,
      minWidth: { xs: 360, sm: 560 }, maxWidth: 720,
      background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[800]})`,
    }}>
      <Typography variant="h5" gutterBottom>Campaign Builder Onboarding</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create app accounts for <strong>{organization?.name}</strong>. Later, this will create Cognito users;
        for now, it stores credentials locally.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom>1) Add a user</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
        <TextField
          label="Full name (optional)"
          value={name}
          onChange={(e)=>setName(e.target.value)}
          size="small"
        />
        <TextField
          label="Email"
          value={email}
          onChange={(e)=>setEmail(e.target.value)}
          size="small"
          error={!!email && !emailRe.test(email)}
          helperText={email && !emailRe.test(email) ? 'Invalid email' : ' '}
        />
        <TextField
          label="Temp password"
          value={pwd}
          onChange={(e)=>setPwd(e.target.value)}
          size="small"
          type="password"
          helperText="Min 8 chars"
        />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="contained" onClick={onCreate} disabled={!canCreate || creating}>
            {creating ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Create User
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom>2) Org users</Typography>
      <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 1 }}>
        {isFetching ? (
          <Typography variant="body2">Loading…</Typography>
        ) : users.length === 0 ? (
          <Alert severity="info">No users yet for this org.</Alert>
        ) : (
          users.map(u => (
            <Paper key={u._id} variant="outlined" sx={{
              p: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'rgba(255,255,255,0.08)'
            }}>
              <Box>
                <Typography variant="body2"><strong>{u.name || '(no name)'}</strong></Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{u.email}</Typography>
              </Box>
              <Tooltip title="Remove user">
                <span>
                  <IconButton size="small" onClick={() => onRemove(u._id)} disabled={deleting}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          ))
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={() => onComplete?.()}>Close</Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: '', sev: 'info' })}
      >
        <Alert severity={snack.sev} sx={{ width: '100%' }}>{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default CBOnboarding;
